import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import {
    ConfirmationDialogComponent,
    DeprecatedModalComponent,
    DialogComponent,
    StepComponent,
    StepsComponent,
} from '@app/components';
import { NavigationGuard } from '@app/guards/navigation.guard';
import { CanDeactivateComponent } from '@app/interfaces/can-deactivate-component.interface';
import { BulkExportEntityService } from '@app/modules/reporting/services';
import { BULK_EXPORT_META_DATA } from '@app/modules/reporting/views/bulk-export/bulk-export-config';
import { AuthService, NotifyService } from '@app/services';
import { AnalyticEvents, AnalyticService } from '@app/services/analytic/analytic.service';
import { TranslatableKey } from '@app/types/translatable.type';
import { StoreView } from '@common/views';
import { BaseForm } from '@forms/base.form';
import { Column } from '@import/classes';
import { MapColumnsForm } from '@import/components';
import { StepChange } from '@import/enums/step-change.enum';
import { ValidationState } from '@import/enums/validation-state.enum';
import { hasValidationErrors } from '@import/functions/rows';
import { EmployeeRowAndColumnService } from '@import/services';
import { ImportService } from '../../services/import/import.service';

enum UploadStatus {
    ready,
    working,
    finished,
    failed,
}

const DOWNLOAD_TEMPLATE_LINK = '/assets/excel/humi-employee-import-template.xlsx';
const NAVIGATION_WARNING = NavigationGuard.warningMessage;

@Component({
    templateUrl: './employees-import.template.html',
    styleUrls: ['./employees-import.style.scss'],
    providers: [BulkExportEntityService],
})
export class EmployeesImportView extends StoreView implements CanDeactivateComponent, OnInit {
    @ViewChild('submissionModal', { static: true }) submissionModal: DeprecatedModalComponent;
    @ViewChild('confirmationDialog', { static: true }) confirmationDialog: ConfirmationDialogComponent;
    @ViewChild('appSteps', { static: true }) stepsComponent: StepsComponent;
    @ViewChild('dependentFieldModal') dependentFieldModal: DialogComponent;
    uploadStatus: UploadStatus = UploadStatus.ready;
    mapColumnsForm: BaseForm = new BaseForm();
    mapValuesForm: BaseForm = new BaseForm();
    reviewForm: BaseForm = new BaseForm();
    uploadStatuses = UploadStatus;
    importCsvTemplate: string;
    lastChange: StepChange;

    newEmployeesCount = 0;
    updateEmployeesCount = 0;
    totalImportCount = 0;
    totalImportFailCount = 0;

    canExportData = false;
    isLoading = false;
    reviewFormFullscreen = false;

    readonly importMetaData = BULK_EXPORT_META_DATA;

    /** Used to display the legend items in step 1 **/
    helpItems: { label: TranslatableKey; link: string }[] = [
        {
            label: 'import.uploadFileStep.helpItemImportYourData',
            link: '4410093502231',
        },
        {
            label: 'import.uploadFileStep.helpItemUseCsv',
            link: '360024509713',
        },
    ];

    constructor(
        protected notify: NotifyService,
        private exportService: BulkExportEntityService,
        private analytics: AnalyticService,
        private importerService: EmployeeRowAndColumnService,
        private auth: AuthService,
        public importService: ImportService
    ) {
        super();
    }

    @ViewChild('mapColumnsForm') set mapColumnsFormContent(content: BaseForm) {
        if (content) {
            this.mapColumnsForm = content;
        }
    }

    @ViewChild('mapValuesForm') set mapValuesFormContent(content: BaseForm) {
        if (content) {
            this.mapValuesForm = content;
        }
    }

    @ViewChild('reviewForm') set reviewFormContent(content: BaseForm) {
        if (content) {
            this.reviewForm = content;
        }
    }

    ngOnInit(): void {
        this.setEntityFromRoute();
        this.analytics.trackEvent(AnalyticEvents.VisitEmployeeImporter);
        this.importCsvTemplate = DOWNLOAD_TEMPLATE_LINK;
        this.canExportData = this.auth.can(['create.file', 'export.employee']);
    }

    openDependentFieldModal(): void {
        this.dependentFieldModal.open = true;
        this.analytics.trackEvent(AnalyticEvents.ImporterDependentFieldModalOpened);
    }

    trackNextStep(details: [StepComponent, number]): void {
        this.analytics.trackEvent(AnalyticEvents.ImporterNextStep);
        this.analytics.trackEvent(this.getStepAnalyticEvent(details[1]));
    }

    trackPreviousStep(details: [StepComponent, number]): void {
        this.analytics.trackEvent(AnalyticEvents.ImporterPreviousStep);
        this.analytics.trackEvent(this.getStepAnalyticEvent(details[1]));
    }

    confirmPrevious = (): Promise<void> => {
        return this.confirmationDialog.open().then((res) => {
            return res ? Promise.resolve() : Promise.reject();
        });
    };

    confirmNext = (step: StepComponent): Promise<void> => {
        /*
         * Before proceeding to the next step on Column Matching, we want the user
         * to confirm that they want to proceed without having columns from the
         * imported file matched to any Humi Column.
         * */
        const columnMatchingStep = 1;
        if (step.parent.activeStep === columnMatchingStep) {
            const stepForm = step.stepForm as MapColumnsForm;
            if (stepForm.remainingColumnCount > 0) {
                return stepForm.remainingColumnConfirmation.open().then((res) => {
                    return res ? Promise.resolve() : Promise.reject();
                });
            }
        }

        return Promise.resolve();
    };

    canDeactivate(): boolean {
        if (this.shouldWarnBeforeNavigation()) {
            return window.confirm(NAVIGATION_WARNING);
        }

        return true;
    }

    @HostListener('window:beforeunload', ['$event'])
    warn($event: BeforeUnloadEvent): void {
        if (this.shouldWarnBeforeNavigation()) {
            $event.returnValue = NAVIGATION_WARNING;
        }
    }

    onStepLoading(isLoading: boolean): void {
        this.isLoading = isLoading;
    }

    onSave(): void {
        if (hasValidationErrors(this.importerService.processedRows)) {
            this.notify.error('import.errorsInData');

            return;
        }

        this.newEmployeesCount = this.importerService.getRowCountByValidationState(ValidationState.new);
        this.updateEmployeesCount = this.importerService.getRowCountByValidationState(ValidationState.update);

        this.submissionModal.show();
    }

    setColumnsAndRows(event: [Column[], string[][]]): void {
        const [columns, rows] = event;
        this.importerService.setColumnsAndRows(columns, rows);
        this.importerService.setLastChange(StepChange.FileUpload);
    }

    confirmUpload(): void {
        this.uploadStatus = UploadStatus.working;
        this.importerService
            .importPayload()
            .then((response) => {
                this.totalImportCount =
                    response.data.statistics.numberSuccessful ?? this.newEmployeesCount + this.updateEmployeesCount;
                this.totalImportFailCount = response.data.statistics.numberFailed ?? 0;

                if (this.totalImportFailCount > 0) {
                    this.uploadStatus = UploadStatus.failed;
                    this.notify.error('import.validationFailedDuringImport');
                    return;
                }

                this.uploadStatus = UploadStatus.finished;
                this.analytics.trackEvent(AnalyticEvents.ImportSuccess);
            })
            .catch((err) => {
                this.analytics.trackEvent(AnalyticEvents.ImportFail);
                this.notify.error(err || 'import.unableToImportData');
            });
    }

    async onExportEmployee(): Promise<void> {
        this.isLoading = true;
        await this.exportService.export();
        this.isLoading = false;
        this.analytics.trackEvent(AnalyticEvents.ImporterStep1FileExport);
    }

    reload(): void {
        window.location.reload();
    }

    private setEntityFromRoute(): void {
        this.exportService.entity = this.importMetaData.find((entity) => entity.routerLink === 'employees');
    }

    private shouldWarnBeforeNavigation(): boolean {
        return this.hasData() && this.uploadStatus !== UploadStatus.finished;
    }

    private getStepAnalyticEvent(stepNumber: number): AnalyticEvents {
        switch (stepNumber) {
            case 0:
                return AnalyticEvents.ImporterStep1;
            case 1:
                return AnalyticEvents.ImporterStep2;
            case 2:
                return AnalyticEvents.ImporterStep3;
            case 3:
                return AnalyticEvents.ImporterStep4;
        }
    }

    private hasData(): boolean {
        return !!this.importerService.columns.length && !!this.importerService.rows.length;
    }
}
